/**
 * MystFlo Design System — Premium 2025 Edition
 * 
 * DESIGN PHILOSOPHY:
 * - Black & white foundation with gray gradients
 * - White/soft-gray glows ONLY (NO colored glows)
 * - Colors for status badges and error states
 * - Lantern (playbook §3) is the one gold carve-out: "needs the founder," never aliased to warning/amber
 * - Luxury, premium, elegant, minimal
 *
 * Lantern values live in kit playbook §3. Never alias lantern to warning/amber.
 *
 * SINGLE SOURCE OF TRUTH for all design tokens.
 *
 * Last Updated: 2026-08-18
 *
 * COMPONENT CATALOG (added 2026-02-20):
 * The `components` section catalogs every CSS utility class from index.css.
 * The `tokenMigration` section maps design tokens → Tailwind equivalents.
 */

export const designSystem = {
  colors: {
    // Base colors - pure black foundation
    background: "#000000",
    surface: "rgba(255, 255, 255, 0.04)",
    surfaceGlass: "rgba(255, 255, 255, 0.08)",
    surfaceElevated: "rgba(255, 255, 255, 0.06)",
    
    // Borders
    border: "rgba(255, 255, 255, 0.10)",
    borderSubtle: "rgba(255, 255, 255, 0.06)",
    borderHover: "rgba(255, 255, 255, 0.15)",
    // WCAG 1.4.11 — used on interactive/empty-state surfaces requiring ≥3:1 vs background.
    // α=0.40 over pure black computes to ~3.6:1 (α=0.20 = 1.66:1, fails 3:1).
    borderElevated: "rgba(255, 255, 255, 0.40)",
    
    // Text hierarchy
    textPrimary: "#FFFFFF",
    textSecondary: "rgba(255, 255, 255, 0.6)",
    textMuted: "rgba(255, 255, 255, 0.4)",
    
    // Glow colors - WHITE/GRAY ONLY (NO colored glows)
    glowWhite: "rgba(255, 255, 255, 0.25)",
    glowSoft: "rgba(255, 255, 255, 0.15)",
    glowSubtle: "rgba(255, 255, 255, 0.08)",
    
    // Accent - White based (backwards compat)
    accent: "#FFFFFF",
    accentDark: "rgba(255, 255, 255, 0.08)", // For inactive button backgrounds
    accentAura: "rgba(255, 255, 255, 0.15)",
    accentSubtle: "rgba(255, 255, 255, 0.08)",
    
    // Status colors - for badges and errors (never for glows).
    // Lantern is not status — see playbook §3. Never alias to warning.
    // success = C12 emerald (Peyton ruling 2026-07-20, #2576) — swept from the
    // old retired shadcn green (hex 22c55e) to #34c07c 2026-07-30
    // (design-sor-consolidation Option A) so this now agrees with
    // index.css --success-emerald.
    success: "#34c07c",
    warning: "#eab308",
    destructive: "#ef4444",
    info: "#3b82f6",
    // Lantern — playbook §3. Needs-you ink. Header Private Beta badge.
    // The only non-status gold. Never alias to warning / amber. One pair.
    lantern: "#D4AF6D",
    lanternInk: "#8A6B33",
    lanternDim: "rgba(212, 175, 109, 0.55)",
    lanternFaint: "rgba(212, 175, 109, 0.12)",
    // Premium action colors
    favorite: "#991b1b", // Premium red for heart/favorite icon (darker, muted)
    activate: "#166534", // Premium green for activation buttons (darker, muted)
  },

  micrographic: {
    dashYou: "none",
    dashDouble: "1.5 4.5",
    doubleOpacity: 0.70,
  },

  layout: {
    sidebarWidth: "208px",
    maxWidth: "1440px",
    maxWidthContent: "1280px", // max-w-7xl
    maxWidthNarrow: "1152px",  // max-w-6xl (for billing, etc.)
    // Page-container TOP padding scalar (mirrors pageLayout.padding top value).
    // Trimmed in T1119; L/R + bottom live in pageLayout.padding (full triplet).
    pagePaddingMobile: "20px",
    pagePaddingTablet: "24px",
    pagePaddingDesktop: "28px",
    sectionSpacingMobile: "24px",
    sectionSpacingTablet: "32px",
    sectionSpacingDesktop: "48px",
    cardPaddingMobile: "16px",
    cardPaddingTablet: "20px",
    cardPaddingDesktop: "24px",
    headerHeight: "72px",
  },

  /**
   * Editorial Book paper + rhythm (#5019 / AC-PB-10 / AC-PB-11).
   * CSS mirror: `src/index.css` `.ink-book` + 768 / 1024 steps.
   * Text uses paper / ink / ink-82 / ink-70 only; -45/-30/-18 are rules.
   */
  bookTokens: {
    paper: { cream: "#f4efe6", ink: "#2e2a26" },
    rhythm: {
      sm: { chTop: "40px", chBot: "36px", block: "24px", facet: "28px", gutter: "20px" },
      md: { chTop: "56px", chBot: "48px", block: "28px", facet: "32px", gutter: "40px" },
      lg: { chTop: "72px", chBot: "64px", block: "32px", facet: "36px", gutter: "72px" },
    },
  },

  // Page Layout Tokens - Unified across all pages
  // IMPLEMENTATION: src/components/PageLayout.tsx
  // All pages MUST use the PageLayout component (not manual .page-container structure)
  pageLayout: {
    maxWidth: "1440px",
    maxWidthContent: "1280px", // max-w-7xl (default)
    maxWidthNarrow: "1152px",  // max-w-6xl (billing, etc.)
    // .page-container padding (top / L-R / bottom). Top trimmed in T1119
    // (viewport-density) so content clears the first viewport; L-R + bottom kept.
    padding: {
      mobile: "20px 20px 32px",
      tablet: "24px 32px 40px",
      desktop: "28px 40px 48px",
    },
    // .page-header margin (mb-3 sm:mb-4 in index.css). Tightened in T1119
    // from the old mb-5/6 — the header-zone slot spacing now carries the rhythm.
    headerMargin: {
      mobile: "12px",  // mb-3
      desktop: "16px", // sm:mb-4
    },
    // CSS classes to use:
    // .page-container - outer wrapper with padding
    // .page-header - standard header (title + description)
    // .page-header--with-actions - header with action buttons
  },

  typography: {
    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    fontFamilyMono: "'Geist Mono', monospace",
    weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700 },
    // pageTitle: real impl is `clamp(24px, 3vw, 28px)` in .text-page-title (index.css);
    // this mirrors the desktop cap. Trimmed 32px → 28px in T1119 (viewport-density).
    // metric: real impl is `clamp(20px, 2.5vw, 26px)` in .text-metric (index.css) — the
    // .metrics-card-value KPI number. Mirrors the desktop cap. Trimmed 28px → 26px in T1125
    // (type weight+scale). Mobile 24px, tablet 26px set via @media in index.css.
    sizes: { pageTitle: "28px", sectionHeading: "24px", cardTitle: "18px", metric: "26px", body: "16px", small: "14px", base: "16px", xs: "12px" },
    // ── Premium Flow top tier (lit-hairline port, 2026-06-08) ──
    // Scale-contrast tier above `display` — fixes wall-of-text hero. ADDITIVE:
    // existing tokens unchanged. Mirrors website .text-hero / .text-eyebrow /
    // .text-display (feat/premium-hero-T506). Hero ceiling = 80px (Peyton's
    // quiet-luxury call, NOT 104px). Geist semibold (600), never bold (700).
    hero: { size: "clamp(40px, 7vw, 80px)", weight: 600, lineHeight: 0.98, letterSpacing: "-0.035em", classes: "text-hero" },
    displayXL: { size: "clamp(36px, 6vw, 72px)", weight: 600, lineHeight: 1.04, letterSpacing: "-0.03em", classes: "text-display-xl" },
    eyebrow: { size: "13px", weight: 500, lineHeight: 1.2, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", classes: "text-eyebrow" },
    display: { size: "clamp(32px, 5vw, 48px)", weight: 600, lineHeight: 1.1, letterSpacing: "-0.02em", classes: "text-4xl font-semibold tracking-tight" },
    heading: { size: "clamp(24px, 3vw, 32px)", weight: 600, lineHeight: 1.2, letterSpacing: "-0.015em", classes: "text-2xl font-semibold" },
    subheading: { size: "clamp(18px, 2.5vw, 24px)", weight: 500, lineHeight: 1.3, letterSpacing: "-0.01em", classes: "text-xl font-medium" },
    body: { size: "16px", weight: 400, lineHeight: 1.6, letterSpacing: "-0.005em", classes: "text-base leading-relaxed" },
    small: { size: "14px", weight: 400, lineHeight: 1.5, letterSpacing: "0", classes: "text-sm text-muted-foreground" },
    label: { size: "13px", weight: 500, lineHeight: 1.4, letterSpacing: "0", classes: "text-sm font-medium" },
    code: { size: "13px", weight: 400, lineHeight: 1.5, classes: "font-mono text-sm" },
    // ── Card/heading type ramp — W1 token foundation (#2576, 2026-07-22) ──
    // Published for the follow-on W3 workstream (post-card + featured-tile
    // oversized-title fix) to point at instead of hand-picking Tailwind
    // classes. TWO distinct tiers, because the assistant-card NAME and the
    // platform's generic card/section title are not the same size in the
    // shipped frames — publishing only, W1 does NOT apply either token to a
    // component (that's W3's job).
    //
    // `size` and `classes` on both tiers are deliberately the SAME value
    // expressed two ways. They must never diverge: W3 will reach for
    // `classes` (Tailwind codebase), so a `classes` string that contradicts
    // `size` silently ships the wrong number. In particular Tailwind's
    // `tracking-tight` is -0.025em, NOT -0.01em — where the ramp calls for
    // -0.01em the class must say `tracking-[-0.01em]` explicitly.
    //
    //   - assistantCardTitle: the assistant NAME on AssistantSummaryCard.pre
    //     and .post — both now point here (G2, #2666). Pre previously used
    //     `CardTitle` (base `text-lg sm:text-xl lg:text-2xl`) with a
    //     `text-[15px]` override that tailwind-merge does NOT cancel across
    //     responsive variants, so desktop rendered at 24px despite the diff
    //     looking correct (CLASS 2, #2666 G0). Fixed to a plain `<h3>` +
    //     this token, same shape as post. NOT a § Core 2 row — the
    //     foundations ramp has no assistant-card-name tier. Grounded instead
    //     in the shipped pre-card (15px/400/leading-snug/tracking-tight) and
    //     corroborated by the explore.html frame's `.a-name` (14.5px/400).
    //     letterSpacing therefore records the pre-card's actual -0.025em, not
    //     Core 2's -0.01em. Flat, not fluid: Core 2 rows convert to fluid
    //     because each carries a floor/ceiling pair (U8); this tier has a
    //     single shipped value and no authored pair to convert.
    //     W3 points the post-card title here and deletes the min-h
    //     reservation (equal card height comes from the grid, not a padded
    //     title).
    //   - cardTitle: the platform's generic card/section-heading tier,
    //     grounded verbatim in foundations Core 2 ("card title":
    //     clamp(16–18px)·500·-0.01em), converted in place to the U8 fluid
    //     ladder (canon U8, BINDING 2026-07-22): container-query units, NEVER
    //     viewport `vw`. Reference container = the PAGE CONTENT COLUMN (the
    //     same box § Core 7's display-type row reads), so every card title on
    //     a surface matches regardless of how wide its own card is.
    //     Coefficient per Core 7: 100 × ceiling ÷ the column width where it
    //     should top out = 100 × 18 ÷ ~950 ≈ 1.9cqi. NOTE for W2/W3: this
    //     resolves correctly only under a `container-type: inline-size`
    //     ancestor on that column — with no container it degrades to the
    //     small viewport, which is the pre-U8 behaviour, not a crash.
    //     W3 points AssistantsFeaturedTile's h2
    //     here — down from its current `text-lg sm:text-xl lg:text-2xl`
    //     (18→24px), the "super large title" Peyton flagged on Assistants.
    //     Additive: the flat legacy `sizes.cardTitle` (18px) stays
    //     untouched — it has unrelated existing consumers
    //     (WhatsAppSetupWizard, OAuthForm) this token does not touch.
    assistantCardTitle: {
      size: "15px",
      weight: 400,
      lineHeight: 1.375, // leading-snug
      letterSpacing: "-0.025em", // = Tailwind `tracking-tight`, as the pre-card ships
      classes: "text-[15px] font-normal leading-snug tracking-tight",
    },
    cardTitle: {
      size: "clamp(16px, 1.9cqi, 18px)",
      weight: 500,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      classes: "text-[clamp(16px,1.9cqi,18px)] font-medium leading-[1.3] tracking-[-0.01em]",
    },
  },

  radius: { sm: "8px", md: "12px", lg: "16px", xl: "20px", full: "9999px", small: "8px", medium: "12px", large: "16px", xlarge: "20px" },

  // SHADOWS - White/gray only (NO colored glows)
  shadows: {
    lift: "0 8px 30px rgba(0, 0, 0, 0.12)",
    liftMd: "0 12px 40px rgba(0, 0, 0, 0.15)",
    liftLg: "0 20px 60px rgba(0, 0, 0, 0.2)",
    // White aura glow for hover effects
    aura: "0 0 30px 4px rgba(255, 255, 255, 0.15)",
    auraSubtle: "0 0 20px 2px rgba(255, 255, 255, 0.10)",
    auraBright: "0 0 40px 6px rgba(255, 255, 255, 0.20)",
    innerGlow: "inset 0 1px 0 rgba(255, 255, 255, 0.1)",
    soft: "0 8px 32px rgba(0, 0, 0, 0.6)",
    softSm: "0 4px 16px rgba(0, 0, 0, 0.5)",
    softLg: "0 12px 48px rgba(0, 0, 0, 0.7)",
    // White glow only
    glow: "0 0 40px rgba(255, 255, 255, 0.20)",
    glowSoft: "0 0 20px rgba(255, 255, 255, 0.12)",
    glowSubtle: "0 0 15px rgba(255, 255, 255, 0.08)",
    // ── Premium Flow material lever 3 (lit-hairline port, 2026-06-08) ──
    // Directional edge-light: the single biggest "lit object vs flat sticker"
    // lever. A 1px top-left highlight + bottom-right shade reads light catching
    // a perimeter. Restraint rule: light the PERIMETER, not the partitions —
    // apply to outer frames/primary tiles, never inner dividers. Mirrors
    // website --edge-light-tl. ADDITIVE.
    edgeLightTl: "inset 1px 1px 0 rgba(255, 255, 255, 0.10), inset -1px -1px 0 rgba(0, 0, 0, 0.4)",
    // ── HowItWorksFlow (T-HIW-FLOW, 2026-06-10; repurposed T1285) ──
    // The single lit object on the workflow chart: edge-light perimeter + a soft
    // WHITE aura (monochrome, no colour tint). Under the glow-is-earned lock
    // (2026-07-04) this is applied ONLY to the outcome tile (.flow-frag--outcome)
    // — the deliverable — so the eye lands on the result; number nodes and AI
    // fragments stay flat hairline. (Name kept for stability; no longer the AI
    // badge.) Consumed via the --flow-node-ai-glow CSS var. ADDITIVE.
    flowNodeAiGlow:
      "inset 1px 1px 0 rgba(255, 255, 255, 0.14), inset -1px -1px 0 rgba(0, 0, 0, 0.4), 0 0 24px 1px rgba(255, 255, 255, 0.10)",
  },

  glass: {
    panel: { background: "rgba(255, 255, 255, 0.04)", blur: "12px", border: "rgba(255, 255, 255, 0.10)", shadow: "0 8px 30px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.08)" },
    card: { background: "rgba(255, 255, 255, 0.06)", blur: "16px", border: "rgba(255, 255, 255, 0.10)", shadow: "0 8px 30px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1)", radius: "16px", innerGlow: "inset 0 1px 0 rgba(255, 255, 255, 0.1)" },
    surface: { background: "rgba(255, 255, 255, 0.04)", blur: "12px", border: "rgba(255, 255, 255, 0.08)", shadow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)", radius: "16px", innerGlow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)" },
    elevated: { background: "rgba(255, 255, 255, 0.06)", blur: "16px", border: "rgba(255, 255, 255, 0.10)", shadow: "0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)", radius: "16px", innerGlow: "inset 0 1px 0 rgba(255, 255, 255, 0.1)" },
    badge: { background: "rgba(255, 255, 255, 0.08)", blur: "8px", innerGlow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)", shadow: "0 2px 8px rgba(0, 0, 0, 0.2)", radius: "8px" },
    hover: { background: "rgba(255, 255, 255, 0.08)", border: "rgba(255, 255, 255, 0.15)", transform: "translateY(-2px)" },
    active: { background: "rgba(255, 255, 255, 0.12)", border: "rgba(255, 255, 255, 0.20)" },
  },

  // EFFECTS - White/gray glows only
  effects: {
    cardHover: { shadow: "0 0 30px 4px rgba(255, 255, 255, 0.15)", transform: "translateY(-2px)", transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)" },
    buttonHover: { shadow: "0 0 20px 2px rgba(255, 255, 255, 0.12)", transform: "translateY(-1px)" },
    focusRing: "0 0 0 2px rgba(255, 255, 255, 0.20)",
    panelBlur: "backdrop-blur-md",
    // `entrance` added (lit-hairline port, 2026-06-08): tier-ordered stagger,
    // transform/opacity only (GPU), honors prefers-reduced-motion. ADDITIVE —
    // fast/default/smooth unchanged.
    transition: { fast: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)", default: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)", smooth: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", entrance: "transform 0.24s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.24s cubic-bezier(0.4, 0, 0.2, 1)" },
    // White glows only (backwards compat names)
    glowPrimary: "0 0 40px rgba(255, 255, 255, 0.20)",
    glowSoft: "0 0 20px rgba(255, 255, 255, 0.12)",
    glowSubtle: "0 0 15px rgba(255, 255, 255, 0.08)",
    glowSuccess: "0 0 20px rgba(255, 255, 255, 0.12)",
    glowAccent: "0 0 20px rgba(255, 255, 255, 0.15)",
    transitionSmooth: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // ── MATERIALS — Premium Flow Phase 1 (lit-hairline port, 2026-06-08) ──
  // NEW additive group. Monochrome depth/light vocabulary mirrored from the
  // website premium pass (feat/premium-hero-T506). Honors texture-tokens-inline:
  // every material lands as a token here, never inline in a component. All
  // white/gray alpha over black — no color, no metal (metal is Phase-2 only).
  // The "lit hairline" line family: light the PERIMETER not the partitions —
  // lineAccent/hairlineStrong are for outer frames + the signature thread;
  // hairline is the flat inner divider. surfaceFaint is the near-transparent
  // fill under a hairline (editorial surface, lighter than glass.card).
  materials: {
    // Lever 1 — gradient-mesh canvas, light falls top-left, over black.
    gradientMesh:
      "radial-gradient(120% 80% at 20% 0%, rgba(255, 255, 255, 0.06) 0%, transparent 60%), radial-gradient(90% 70% at 85% 25%, rgba(255, 255, 255, 0.025) 0%, transparent 55%)",
    // Lever 2 — fine grain overlay (~3% opacity, mix-blend overlay). The full
    // tiling SVG noise + blend lives in the .grain-overlay utility (index.css);
    // this token records the opacity contract for programmatic reference.
    grainOpacity: 0.03,
    // The lit fine-line vocabulary (restraint pass).
    lineAccent: "rgba(255, 255, 255, 0.10)",      // the single sweeping line accent / lit perimeter thread
    hairline: "rgba(255, 255, 255, 0.07)",        // FLAT inner divider / surface border
    hairlineStrong: "rgba(255, 255, 255, 0.12)",  // hover / emphasis edge (lit)
    surfaceFaint: "rgba(255, 255, 255, 0.02)",    // near-transparent fill under a hairline
    surfaceFaintHover: "rgba(255, 255, 255, 0.04)",
  },

  spacing: { xs: "4px", sm: "8px", md: "16px", lg: "24px", xl: "32px", "2xl": "48px", "3xl": "64px", "4xl": "72px", "5xl": "96px", hairline: "1px", xxl: "48px" },
  breakpoints: { sm: "640px", md: "768px", lg: "1024px", xl: "1280px", "2xl": "1536px" },
  
  // Responsive Design Guidelines - Mobile First (Audit: 2025-12-09)
  // IMPLEMENTATION: All pages must use PageLayout component with these patterns
  // BREAKPOINTS: sm=640px, md=768px, lg=1024px, xl=1280px
  responsive: {
    // Page containers - ALWAYS prevent horizontal scroll
    containerOverflow: "overflow-x-hidden",
    
    // Text overflow handling
    textOverflow: {
      singleLine: "truncate",           // Titles, names that may overflow
      multiLine: "line-clamp-2",        // Descriptions, multi-line content
      breakWords: "break-words",        // Long URLs, emails, IDs
    },
    
    // ─── LAYOUT PRINCIPLE — Vertical-first (codified 2026-05-09 by Peyton) ───
    //
    // Brand DNA: premium, clean, luxury, breathability, class.
    // Vertical stacks express breathability. Multi-column tile rows feel cheap
    // and dashboard-y. Founders scan top-to-bottom — they ask "give me a list."
    //
    // DEFAULT: vertical stack (`space-y-*` + `flex-col`).
    // Use `verticalList` for lists of options/actions/sections.
    //
    // HORIZONTAL GRIDS allowed ONLY for:
    //   - Catalog/gallery views (Assistants card grid, Connects tile grid)
    //   - Comparison tables (≤3 items where direct comparison matters)
    //   - Statistics dashboards / KPI rows
    //   - Form field pairs that genuinely belong side-by-side
    //
    // AVOID:
    //   - Rows of 3-4+ tiles for picking/filling/navigating options
    //   - Side-by-side action cards with rich descriptions
    //   - "Pick one of these" rendered as columns
    //
    // WHEN IN DOUBT: vertical. Breathing room beats density.
    //
    // Grid patterns - mobile-first (base = mobile)
    gridPatterns: {
      cards: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",       // Catalog/gallery (Assistants, Connects) — OK
      metrics: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",     // KPI dashboards — OK
      forms: "grid-cols-1 sm:grid-cols-2",                      // Form field pairs — OK when fields belong together
      actions: "flex flex-col sm:flex-row gap-2 sm:gap-3",      // Button groups (≤3 buttons) — OK
      // Vertical-first patterns (PREFERRED for option-picking, lists, sections):
      verticalList: "space-y-2 sm:space-y-3",                   // Stacked list rows
      verticalListDivided: "divide-y divide-white/[0.06]",      // Single container, divided rows (settings-list aesthetic)
      verticalStack: "flex flex-col gap-3 sm:gap-4",            // Vertical flow with consistent gap
    },
    
    // Flex patterns - mobile stacking
    flexPatterns: {
      cardContent: "flex flex-col sm:flex-row sm:items-center justify-between gap-4",
      headerActions: "flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4",
      buttonGroup: "flex flex-col sm:flex-row gap-2 sm:gap-3",
    },
    
    // Table handling - ALWAYS wrap in scrollable container
    tables: {
      wrapper: "overflow-x-auto",        // Outer scroll container
      minWidth: "min-w-[600px]",         // Ensure table has minimum width
      scrollHide: "scrollbar-hide",      // Hide scrollbar on mobile
    },
    
    // Tabs and filters - horizontal scroll on mobile
    tabs: {
      container: "overflow-x-auto scrollbar-hide",
      content: "flex gap-2 min-w-max",
    },
    
    // Charts - ALWAYS use ResponsiveContainer from recharts
    charts: {
      wrapper: "w-full h-[200px] sm:h-[250px] lg:h-[300px]",
      container: "ResponsiveContainer width={100%} height={100%}",
    },
    
    // Responsive padding scale
    padding: {
      container: "p-4 sm:p-6 lg:p-8",
      card: "p-4 sm:p-6",
      section: "py-4 sm:py-6 lg:py-8",
    },
    
    // Responsive gap scale
    gaps: {
      grid: "gap-3 sm:gap-4 lg:gap-6",
      flex: "gap-2 sm:gap-3 lg:gap-4",
      section: "space-y-4 sm:space-y-6 lg:space-y-8",
    },
    
    // Button sizing
    buttons: {
      fullOnMobile: "w-full sm:w-auto",
      minWidth: "min-w-[120px]",
    },
    
    // Badge/chip groups - wrap on mobile
    badges: {
      group: "flex flex-wrap gap-2",
      shrink: "flex-shrink-0",
    },
  },
  // Z-Index Hierarchy — the single narrative of the overlay stack (#2576 Wave
  // 1a re-stack per overlays.html frame 9; numeric values unchanged from
  // 2025-12-12, this is a documentation pass, not a renumbering).
  //
  //   12000  receipt-style sheet STACKED ABOVE a slide-over (content)
  //   10500  receipt-style sheet STACKED ABOVE a slide-over (backdrop);
  //          also: tooltips
  //   10300  DrillOver (common/DrillOver.tsx) — fixed, full-viewport, masthead
  //          STRIPPED on purpose (it replaces the header with its own
  //          backline; the artifact's F4/F9 slide-over). This is the ONLY
  //          component at a fixed 10300 — PageSlideOver (below) is
  //          host-relative, not this. BillingDrillOver.tsx and Connects'
  //          ConnectionDetailPanel are both thin `parent=` wrappers around
  //          this one shared shell — extracted 2026-07-23 (#2576 Wave 2),
  //          zero behavior change to either consumer.
  // host+1000 PageSlideOver (Case A, `nested.zGap`) — a 60/40 SPLIT pane, NOT
  //          masthead-stripped: per its own Peyton-locked model (2026-06-14)
  //          the global header stays visible and both panes dock BELOW it
  //          (`top: calc(var(--header-height)+var(--banner-height))`). Its z
  //          is the host panel's z + 1000 (10500 from a 9500 detail modal,
  //          12500 from the 11500 wizard) — never a fixed literal. Whether
  //          PageSlideOver should migrate to the masthead-stripped model is
  //          an open Wave 2 Brain decision, not settled by this comment.
  //   10000  Global header — ABOVE every default sheet/dialog
  //    9500  Sheet/dialog CONTENT (default) — modalContent, BELOW the header
  //    8000  Sheet/dialog BACKDROP (default) — modalBackdrop, BELOW the header
  //    1200  Mobile sidebar drawer
  //    1100  Sidebar backdrop
  //       0  Page content (base)
  //
  // Two different intents at the header line: DrillOver's fixed 10300
  // clears the header on purpose (masthead stripped, it IS the page); every
  // default sheet/dialog sits at 9500/8000, BELOW the header, on purpose (C1
  // — the header stays visible above them). C1 itself isn't a z-index trick:
  // ModalWrapper's backdrop is `top`-scoped to clear the header in the DOM,
  // so there's no pixel overlap with the header rectangle to resolve in the
  // first place — the z-order above only matters once two overlays actually
  // compete for the same region. EXCEPTION: a modal stacked above the header
  // (zIndexOffset lifting it past 10000, e.g. the receipt-over-drill-over
  // case) reverts its backdrop/content to full coverage from 0 — see the
  // `backdropZIndex > designSystem.zIndex.header` check in ModalWrapper.tsx.
  //
  // STACKED CASE (a sheet opened ON TOP of an already-open slide-over — e.g.
  // a receipt sheet over the Wallet-activity drill-over): reuse
  // `zIndexOffset` on the child ModalWrapper (9500+offset / 8000+offset) so
  // its backdrop/content clear the slide-over's fixed 10300, and have the
  // slide-over host suppress its own Esc handling while the child is open
  // (`suppressEsc`, see common/DrillOver.tsx) so one Esc press closes the
  // child only. This is the ONE stacking mechanism for "sheet over
  // slide-over" — don't fork a second one; `BillingDrillOver.tsx`'s
  // `zIndexOffset={2500}` + `suppressEsc` is the reference call site.
  zIndex: {
    base: 0,
    dropdown: 50,
    sticky: 100,
    fixed: 200,
    sidebarBackdrop: 1100,
    sidebar: 1200,
    // Modal System - All modals BELOW header (header at 10000)
    modalBackdrop: 8000,
    modalContent: 9500, // BELOW header so header stays visible
    header: 10000,      // Header ABOVE all modals
    modalNestedStep: 100, // +100 per nesting level
    tooltips: 10500,
  },
  transitions: { default: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)", smooth: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", fast: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)", modal: "all 0.18s ease" },
  grid: { columns: { mobile: 1, tablet: 2, desktop: 3 }, gap: { mobile: "16px", tablet: "20px", desktop: "24px" } },
  iconography: { sizes: { xs: "12px", sm: "16px", md: "20px", lg: "24px", xl: "32px" }, colors: { default: "#ffffff", success: "#34c07c", warning: "#eab308", error: "#ef4444", muted: "rgba(255,255,255,0.4)" }, strokeWidth: "1.5px" },
  icons: { sizes: { xs: "12px", sm: "16px", md: "20px", lg: "24px", xl: "32px" }, strokeWidth: "1.5px" },

  sidebar: {
    width: { desktop: "208px", mobile: "100%" },
    background: "rgba(0, 0, 0, 0.95)",
    blur: "16px",
    border: "rgba(255, 255, 255, 0.08)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
    backdrop: { color: "rgba(0, 0, 0, 0.6)", blur: "8px" },
    // White glow only for nav items
    navItem: { padding: "14px 20px", gap: "12px", hoverGlow: "0 0 20px rgba(255, 255, 255, 0.15)", activeBackground: "rgba(255, 255, 255, 0.95)", activeColor: "#000000", activeGlow: "0 0 24px -6px rgba(255, 255, 255, 0.25)" },
    animation: { duration: "150ms", easing: "ease-out" },
  },
  sidebarTokens: {
    width: { desktop: "208px", mobile: "100%" },
    background: "rgba(0, 0, 0, 0.95)",
    blur: "16px",
    border: "rgba(255, 255, 255, 0.08)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
    backdrop: { color: "rgba(0, 0, 0, 0.6)", blur: "8px" },
    // White glow only for nav items
    navItem: { padding: "14px 20px", gap: "12px", hoverGlow: "0 0 20px rgba(255, 255, 255, 0.15)", activeBackground: "rgba(255, 255, 255, 0.95)", activeColor: "#000000", activeGlow: "0 0 24px -6px rgba(255, 255, 255, 0.25)" },
    animation: { duration: "150ms", easing: "ease-out" },
    zIndex: { backdrop: 1100, panel: 1200 },
  },

  modal: {
    backdrop: { color: "rgba(0, 0, 0, 0.75)", blur: "12px" },
    background: "rgba(18, 18, 20, 0.98)",
    blur: "24px",
    border: "rgba(255, 255, 255, 0.10)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
    size: { sm: "420px", md: "640px", lg: "900px", maxHeight: "85vh", maxHeightMobile: "95vh", maxWidthDesktop: "900px", maxWidthTablet: "760px", maxWidthMobile: "100vw", maxHeightDesktop: "85vh" },
    radius: { desktop: "16px", mobile: "20px 20px 0 0" },
    padding: { desktop: "24px", tablet: "20px", mobile: "16px" },
    spacing: { padding: "24px" },
    typography: { titleSize: "clamp(16px, 2vw, 20px)", bodySize: "clamp(13px, 1.6vw, 15px)" },
    // ActivationWizard self-elevates by +elevatedOffset (PR #321 — to render above
    // the pre-activation detail modal). Any nested modal opened FROM the wizard
    // (e.g. ConnectionModal during Step 1 reconnect) MUST add this same offset to
    // its computed z-index, otherwise it sits behind the wizard.
    zIndex: { backdrop: 8000, content: 9500, nestedStep: 100, elevatedOffset: 2000 },
    transition: "all 0.2s cubic-bezier(0.32, 0.72, 0, 1)",
  },
  modalTokens: {
    backdrop: { color: "rgba(0, 0, 0, 0.75)", blur: "12px" },
    background: "rgba(18, 18, 20, 0.98)",
    blur: "24px",
    border: "rgba(255, 255, 255, 0.10)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
    size: { maxWidthDesktop: "900px", maxWidthTablet: "760px", maxWidthMobile: "100vw", maxHeightDesktop: "85vh", maxHeightMobile: "95vh" },
    spacing: { padding: { desktop: "24px", tablet: "20px", mobile: "16px" } },
    radius: { desktop: "16px", mobile: "20px 20px 0 0" },
    typography: { titleSize: "clamp(16px, 2vw, 20px)", bodySize: "clamp(13px, 1.6vw, 15px)" },
    // ActivationWizard self-elevates by +elevatedOffset (PR #321 — to render above
    // the pre-activation detail modal). Any nested modal opened FROM the wizard
    // (e.g. ConnectionModal during Step 1 reconnect) MUST add this same offset to
    // its computed z-index, otherwise it sits behind the wizard.
    // Full stack narrative (slide-over 10300, header 10000, this 9500/8000,
    // stacked-sheet-over-slide-over via zIndexOffset+suppressEsc): see the
    // top-level `zIndex` block comment above — one narrative, not duplicated.
    zIndex: { backdrop: 8000, content: 9500, nestedStep: 100, elevatedOffset: 2000 },
    transition: "all 0.2s cubic-bezier(0.32, 0.72, 0, 1)",
    // Nested-modal stacking — single source of truth for any popup that
    // opens on top of another modal (ConnectionModal over wizard,
    // ConnectionModal over AssistantDetail, helper-modal over wizard).
    // Host sets nested wrapper z = <parent panel z> + nested.zGap and
    // applies parentRecede to its panel while nested is open. Do NOT
    // wrap with isolation:'isolate' — the z-gap is enough and isolation
    // caused stacking-context bugs (lesson: stacking-context-isolation-cap).
    nested: {
      backdrop: { color: "rgba(8, 8, 10, 0.96)", blur: "20px" },
      parentRecede: {
        opacity: 0.3,
        filter: "blur(2px)",
        transform: "scale(0.985)",
        transition:
          "opacity 200ms cubic-bezier(0.32, 0.72, 0, 1), filter 200ms cubic-bezier(0.32, 0.72, 0, 1), transform 200ms cubic-bezier(0.32, 0.72, 0, 1)",
      },
      zGap: 1000,
    },
    // Modal Behavior Configuration
    behavior: {
      headerAlwaysVisible: true,
      headerClickClosesAll: true,
      escClosesAll: true,
      backdropClickClosesAll: true,
      maxNestingDepth: 2,
      animation: "scale",
    },
    // Mobile-specific (Bottom Sheet Pattern)
    mobile: {
      presentation: "bottom-sheet",
      height: "95vh",
      borderRadius: "20px 20px 0 0",
      animation: "slide-up",
      dragHandle: true,
      dragToClose: true,
    },
    // Desktop-specific (Centered Dialog)
    desktop: {
      presentation: "centered",
      animation: "scale",
    },
    // ── Spatial slide choreography (T1086, Peyton-locked 2026-06-12) ──
    // Big surfaces stop popping in centered and instead carry MEANING by
    // direction: assistant setup RISES from the bottom (near-full sheet over the
    // dimmed page); "Edit in Brain" slides the real Brain page in FROM THE LEFT
    // (Brain lives in the left nav — spatial coherence). Pilot scope = the
    // activation flow only. Small sm/md confirms STAY centered dialogs.
    //
    // Height anchors are DEFINITE (fixed/inset positioning) — never maxHeight
    // only — so percentage-height chains inside resolve (lesson
    // percentage-height-chain-needs-definite-anchor). All transform/opacity
    // animation (GPU), honoring prefers-reduced-motion via the index.css guard.
    slide: {
      // The activation sheet: rises from the bottom, near-full height. Its top
      // edge sits below the global header + a small reveal gap so the dimmed
      // page peeks above (the context cue). Uses --header-height at runtime.
      sheet: {
        revealGap: "20px", // visible dimmed-page strip above the sheet on desktop
        radius: "20px 20px 0 0", // rounded top corners (sheet metaphor)
        // Dark-transparent glass — KEEP (Peyton-explicit, T1086d). The near-full
        // sheet stays glassmorphic over the dimmed page; do NOT flatten to opaque
        // just because the surface got bigger. The reveal gap reads as
        // glass-over-page, not a hard edge. Pairs with backdrop-blur(24px).
        background: "rgba(10, 10, 10, 0.95)",
        // BORDERLESS (T1249/#1526, Peyton 2026-07-01 — supersedes the T1086d
        // 0.08 ring): the border + lit top hairline made the full-screen sheet
        // read as a card with a "weird border line". Edge separation is carried
        // by the drop shadow + the dimmed page behind — no drawn line. This is
        // the ONE sheet material for every surface (desktop sheet, both mobile
        // sheet layouts, video player) — never re-add a per-surface border.
        border: "none",
        shadow: "0 -24px 60px -12px rgba(0, 0, 0, 0.6)",
        // 0.3s smooth curve = designSystem.transitions.smooth (cubic-bezier).
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // T1086d — the sheet goes NEAR-FULL-WIDTH (the "main screen on a sheet"),
        // NOT a centered 1100px card (that's why the shipped sheet "just slid up
        // the cramped modal" — lesson slide-metaphor-without-the-space). The cap
        // moved INSIDE the sheet: the SURFACE spans the viewport; the READING
        // column inside is bounded for legibility so long-form text never
        // stretches edge-to-edge on a wide screen. The old panel cap
        // `maxWidthDesktop: 1100px` is removed. (`modalTokens.size.maxWidthDesktop`
        // 900px and `modalTokens.slide.brain.maxWidthDesktop` 1280px are different
        // tokens and stay untouched — §7.4 collision note.)
        //
        // T1086e (Peyton-locked 2026-06-13) — the sheet now runs HORIZONTALLY
        // EDGE-TO-EDGE: the L/R gutters drop to 0. Because the reading column is
        // already capped at contentMaxWidth (centered), side gutters never changed
        // content width — they only showed a thin decorative dimmed-page strip at
        // the sides (a redundant repeat of the TOP reveal gap). Edge-to-edge reads
        // as "took the screen". The TOP reveal gap STAYS (it's the slide-down-to-
        // return cue). Token kept (not deleted) so all three sheets stay token-
        // first; semantic is "L/R viewport inset", now 0.
        gutterDesktop: "0px", // edge-to-edge: no L/R viewport inset (T1086e)
        contentMaxWidth: "960px", // INNER readability cap for the scroll column
        contentMaxWidthSplit: "1440px", // wider cap for the 2-column Personalize split-pane (form + live preview); the 960px readability cap is for single-column text only
        // T1147 (Peyton-locked) — the detail modals (post + pre) DROP the 960px
        // OUTER cap so header / tabs / split-pane / metrics / tables / logs run
        // FULL-WIDTH with comfortable page gutters (px-4 sm:px-6 lg:px-8). The
        // re-centered-narrow-column-with-big-side-margins look defeated the
        // slide-for-space purpose. The readability cap now applies ONLY to long
        // PROSE blocks (Overview "What it does" copy, "Your setup" description) —
        // ~65–72ch. Wide content gets the screen; only paragraph runs are bounded.
        // contentMaxWidth STAYS — ModalWrapper + the ActivationWizard sheet still
        // cap their outer column with it (form/confirm surfaces, not prose pages).
        proseMaxWidth: "720px", // readability cap for long prose runs only (~65–72ch)
        // ── Drag-down-to-peek + flick-to-dismiss (T1086e, gesture 1 + 3) ──
        // NOTE (T1147): the desktop drag handle is RETIRED (SheetDragHandle now
        // renders null) — the desktop sheet dismisses via Esc / close / backdrop.
        // The MOTION tokens below stay (still read by useDragSheet for the MOBILE
        // sheet drag). The visible-handle-pill tokens were removed (orphaned once
        // the desktop handle was nulled; the mobile pill uses inline tailwind).
        // Mobile drag mechanics — the founder grabs the top region /
        // drag handle and drags DOWN: the sheet translateY's 1:1 (peek), release
        // past dismissThreshold OR a downward flick > flickVelocity closes it,
        // otherwise it springs back. Read by `useDragSheet` — ZERO inline numbers
        // in the hook (lesson texture-tokens-inline). All transform-driven (no
        // width/height/top — AC-SLIDE-PEEK-PERF); the settle tween honors
        // prefers-reduced-motion (instant snap).
        peekMax: "96px", // 1:1 drag distance before the pull damps (gesture-1 peek)
        peekDampFactor: "0.6", // beyond peekMax the drag is damped by this factor (1:1 ≤ peekMax, ×0.6 past it) — token-first, no inline literal in useDragSheet
        dismissThreshold: "240px", // drag-down displacement that dismisses
        flickVelocity: "0.6", // px/ms downward flick that dismisses regardless of distance
        flickStallWindow: "100ms", // if the pointer holds still longer than this before lift, velocity reads 0 (hold-then-lift = spring back, not flick)
        // ── Sheet chrome (#2248 sheet standard) — the mirror source of truth ──
        // These are the responsive triples that `src/index.css :root` mirrors into
        // the `--sheet-*` custom properties (two media queries: ≥640px, ≥1024px).
        // Components consume the `.sheet-*` CLASSES only, never these values — but
        // the numbers live here per the tokens-only lock, and a vitest asserts the
        // CSS mirror equals these. `contentMaxWidth`/`proseMaxWidth` above STAY
        // (the hand-rolled detail modals still read them) — `chrome` is additive.
        // Band HEIGHT is deliberately NOT a token (#2248 §3a) — it derives from
        // py-2 + the type + the optional subtitle, exactly like the detail-sheet
        // band. A title-only band is naturally shorter than one carrying a
        // subtitle, and that is correct. The withdrawn first draft's fixed 56px
        // `headerH` is gone.
        chrome: {
          gutter:    { mobile: "16px", tablet: "24px", desktop: "40px" }, // L/R page gutter — band + column (desktop 32→40, #2248 polish)
          bodyBlock: { mobile: "16px", tablet: "20px", desktop: "24px" }, // top-under-band + bottom
          title:     { mobile: "14px", desktop: "16px" }, // small + airy (T1053) — NOT a dialog clamp
          column:    "1120px",   // the WORK measure (was contentMaxWidth 960)
          prose:     "720px",    // READING measure (mirrors existing proseMaxWidth)
          // The in-page TAB STRIP height — the SECOND continuous line (#2248,
          // 2026-07-14). Unlike the header band (whose height is deliberately
          // content-derived, see above), the tab strip carries ONE line of type
          // and nothing else, so a fixed height IS knowable — and it has to be,
          // because two DIFFERENT tab implementations sit side by side in the
          // 40:60 split (shadcn `TabsList` in the slid-in page pane vs the
          // hand-rolled strip in the detail modals). They were 40px and 36px, so
          // the second hairline staggered across the seam. One token, both
          // strips, one line. 44px below the split breakpoint keeps the touch
          // target legal (BINDING); 36px on desktop matches the shipped T1053
          // compact strip (a 36px pointer target still passes WCAG AA).
          tabStrip:  { mobile: "44px", desktop: "36px" },
          // The Ask Sheet's ~40:60 vertical split (BrainAskSheet, #2382 follow-up).
          // The top CONTEXT pane ("your section so far") is capped so it can never
          // eat the ask flow: it takes at most this height then scrolls internally,
          // leaving the rest (~60%) for the question card. A cap, not a fixed
          // height — a short section shrinks the pane, it never pads it. Mirrored
          // into `--sheet-ask-context-max` in index.css :root (asserted by
          // sheetChrome.mirror.test.ts). Consumed via the `.ask-context-pane`
          // class — never an inline `max-h-[40vh]` literal (archetype §f #17).
          askContextMaxVh: "40vh",
          // Header utility cluster gap (#2498 — Stripe Workbench parity). The
          // optional `headerActions` slot renders a right-aligned cluster of icon
          // buttons BEFORE the close (utilities ··· close last). The buttons are
          // already 44×44 tap targets, so this is a small visual separator between
          // them, not a layout gap. Mirrored into `--sheet-action-gap`; consumed
          // via `.sheet-header__actions` — never an inline gap literal (§f #17).
          actionGap: "4px",
          // Pinned CTA footer vertical padding (#2498). The optional `footer` slot
          // pins OUTSIDE the scroll container at the sheet's bottom edge with a
          // hairline top separation (INTERNAL partition — the sheet's OUTER edge
          // stays borderless per T1249). On mobile the footer also adds
          // env(safe-area-inset-bottom). Mirrored into `--sheet-footer-pad`;
          // consumed via `.sheet-footer` — never an inline padding literal (§f #17).
          footerPadding: "16px",
          // Header band vertical padding (#2498). The band HEIGHT stays
          // content-derived (title + optional subtitle) — this only sets the
          // breathing room above/below that content. Was py-2 (8px), which
          // top-crammed a subtitle-less title under the mobile drag pill (Peyton
          // caught it on AuthModal's "Sign in to run assistants"). Calibrated to
          // Stripe's Workbench band — comfortable, not fat. Responsive triple
          // (mobile/tablet/desktop) mirrored into `--sheet-header-pad`; consumed
          // via `.sheet-header` — never an inline padding literal (§f #17).
          headerPad: { mobile: "12px", tablet: "14px", desktop: "16px" },
        },
      },
      // The Brain left-slide layer: covers ~88vw, leaving a peek edge of the
      // sheet on the right (the peek IS the context cue + tap-to-return).
      brain: {
        widthDesktop: "88vw", // ~85–90% locked range
        maxWidthDesktop: "1280px",
        background: "rgba(8, 8, 10, 0.98)",
        // Stacked-tier (<split breakpoint) full-page push MUST be opaque
        // (#3598 design review): at 0.98 alpha the host sheet's dense text
        // bleeds through as legible ghosting at near-black levels — dark-theme
        // gamma makes even 2% seepage read as broken layering on the sparse
        // Top-up pane. The desktop split keeps `background` (it sits over the
        // black page, where the seepage is invisible); the mobile push covers
        // the UNDIMMED host sheet, so it uses this solid value.
        backgroundStacked: "rgb(8, 8, 10)",
        borderRight: "1px solid rgba(255, 255, 255, 0.10)",
        shadow: "24px 0 60px -12px rgba(0, 0, 0, 0.7), inset -1px 0 0 rgba(255, 255, 255, 0.08)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // The interactive "peek" strip of the sheet still visible on the right.
        // Tapping it (or Esc) closes the Brain layer back to the sheet.
        peekWidth: "12vw",
      },
      // ── Side-by-side SPLIT (T1086j, mirrored 2026-06-23) ──
      // Peyton REVERSED the layered-overlay model (brain.* above — 88vw layer
      // over an inert rear, peek-edge tap-to-return). On desktop/tablet the
      // slide-over is a 60/40 SIDE-BY-SIDE split where BOTH panes are LIVE.
      // 2026-06-23 Peyton-lock: the orientation is MIRRORED — the slid-in page
      // docks on the LEFT (next to the left nav, where navigation originates),
      // the setup sheet docks on the RIGHT. #1956 (2026-07-04, Peyton flag —
      // "weird gap + back on the wrong panel"): both panes rise FLUSH to the
      // header band (NO revealGap in split — the gap showed a dead black band),
      // both top corners SQUARE (docked workspace, not floating card), and the
      // PAGE PANE owns its own slim Back bar — the back affordance lives on the
      // panel that slid in, never inside the host's assistant header. The old
      // full-width top bar (covered the global nav) is retired; the global app
      // header stays visible in split mode:
      //   LEFT 40%  = the REAL Brain / Team / Pulse page (masthead stripped),
      //               interactive, with its own Back bar on top.
      //   RIGHT 60% = the assistant SETUP (the host sheet — wizard / detail
      //               modal), stays interactive (NOT inert).
      //               Each pane scrolls independently.
      // A thin lit hairline divides them (the page pane's RIGHT edge / the
      // sheet's LEFT edge). Fixed 60/40 — NO drag-to-resize (that's T1086h,
      // intentionally not built). The brain.* overlay tokens stay (mobile +
      // history reuse), this is the desktop model.
      //
      // BOTH widths are fixed % of the split surface, NOT viewport-anchored
      // height — each pane is positioned fixed/inset top→bottom so its
      // percentage-height chain resolves against a DEFINITE anchor (lesson
      // percentage-height-chain-needs-definite-anchor). No inline %/px in the
      // components or the host (lesson texture-tokens-inline).
      split: {
        setupWidth: "60%", // RIGHT pane (host setup) width — documents intent;
        //                    the host derives its panel as `left: pageWidth`.
        pageWidth: "40%", // LEFT pane (real page) width — fixed; also the host
        //                   sheet's `left` inset in split mode.
        // Min viewport width at which the split engages. Below this the slide-over
        // stays a full-screen stacked push (no room for two columns). Consumed by
        // useIsSplitViewport() — this token is the single source for the breakpoint.
        engageBreakpoint: "1024px",
        // Structural divider between the two live panes (no box — single line).
        // = materials.hairlineStrong (0.12) — in-vocabulary, the visible-partition
        // weight, NOT a freshly-minted value. Sits on the page pane's RIGHT edge
        // (= the sheet's LEFT edge) now that the page docks LEFT.
        divider: "1px solid rgba(255, 255, 255, 0.12)",
        // The left (page) pane surface. Slightly distinct from the host glass so
        // the two live panes read as separate workspaces without a heavy border.
        pageBackground: "rgba(8, 8, 10, 0.98)",
        // Cast RIGHT — the page pane is left-docked, so its shadow falls toward
        // the sheet / page centre.
        pageShadow: "24px 0 60px -12px rgba(0, 0, 0, 0.5)",
        // (topBarBackground/topBarBorder retired in #1956 — the full-width top
        // bar is gone; the page pane's Back bar uses `divider` for its rule.)
        // #1956 (2026-07-13): the host sheet does NOT move by `transform` when the
        // split engages — it re-insets its `left` (0 → 40% as the page pane docks
        // LEFT), drops its `top` reveal gap (flush), and squares its `borderRadius`.
        // The old `transform`-only transition animated NONE of those, so the sheet
        // JUMPED to the split layout while the page pane glided in — the "stuck/
        // jumpy" report. Transition the properties that actually change, on the
        // SAME 0.45s expo curve as `.animate-page-pane-in` so both panes settle as
        // one. (Not GPU-composited, but a one-shot 450ms open/close tween, not a
        // scroll-linked animation.)
        // `right` added #2949 (2026-07-30): DrillOver is the first split consumer
        // whose `right` inset actually changes (64px peek → 0) between states —
        // every earlier consumer (ModalWrapper, ActivationWizard) holds `right`
        // constant, so this was a silent no-op for them until DrillOver exposed it.
        transition:
          "left 0.45s cubic-bezier(0.16, 1, 0.3, 1), top 0.45s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.45s cubic-bezier(0.16, 1, 0.3, 1), right 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
      },
      // Backdrop dim+blur shared by the sheet (over the page) and used as the
      // base scrim. Matches the existing modal scrim weight.
      scrim: { color: "rgba(0, 0, 0, 0.80)", blur: "8px" },
    },
  },

  // CONNECTION STATUS — single primitive used by ConnectionStatusButton.
  // Status colours are allowed here (same exception as badges): they encode
  // severity for OAuth/credential health on the Connections surface and are
  // the only place the founder sees red/amber/emerald in connect rows.
  // Added 2026-05-01 (T336).
  //
  // Severity colour rule:
  //   healthy  → emerald  (Tailwind 400/500 family)
  //   warning  → amber    (Tailwind 400/500 family — user can act now)
  //   critical → red      (Tailwind 400/500 family — token broken)
  //   pending  → amber    (same colour as warning — waiting on provider, no
  //                        Reconnect verb. Distinguishes by label, not colour.)
  //
  // The "color carries severity" pattern means:
  //   - No icons inside the button itself.
  //   - Same emerald/amber/red palette is reused for the status word in the
  //     row's health-line copy ("Token expires in 5d") so the row reads as
  //     one signal, not three.
  //   - Background tints stay at /10 alpha; borders at /30. These pass WCAG
  //     AA contrast against the page background (rgba(255,255,255,0.04)).
  connectionStatus: {
    notConnected: {
      // Solid white CTA (state 1) — same primary action treatment as the rest
      // of the platform's "go" buttons (Add payment, Continue, etc.).
      label: 'Connect',
      bg: 'bg-white',
      text: 'text-black',
      border: 'border-transparent',
      hover: 'hover:bg-white/90',
    },
    healthy: {
      label: 'Connected',
      // Trigger background, text, border (Tailwind utility strings).
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-400',
      border: 'border-emerald-400/30',
      hover: 'hover:bg-emerald-500/15',
      // Health-line word colour (used in row description copy).
      copyText: 'text-emerald-400',
      // Raw hex equivalents for places that need an inline style (icons, glows).
      hex: '#34d399',
    },
    warning: {
      label: 'Reconnect',
      bg: 'bg-amber-500/10',
      text: 'text-amber-400',
      border: 'border-amber-400/30',
      hover: 'hover:bg-amber-500/15',
      copyText: 'text-amber-400',
      hex: '#fbbf24',
    },
    critical: {
      label: 'Reconnect',
      bg: 'bg-red-500/10',
      text: 'text-red-400',
      border: 'border-red-400/30',
      hover: 'hover:bg-red-500/15',
      copyText: 'text-red-400',
      hex: '#f87171',
    },
    pending: {
      // Same colour family as warning — pending is the *waiting* form of an
      // amber-severity event. Verb changes ("Pending"), colour does not.
      label: 'Pending',
      bg: 'bg-amber-500/10',
      text: 'text-amber-400',
      border: 'border-amber-400/30',
      hover: 'hover:bg-amber-500/15',
      copyText: 'text-amber-400',
      hex: '#fbbf24',
    },
    // Shared trigger geometry.
    trigger: {
      // 28px tall on default, ≥44px touch target enforced via min-h on mobile
      // by the component itself.
      base: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50',
      sm: 'h-7 text-[11px] px-2.5 gap-1',
      md: 'h-8 text-xs px-3 gap-1.5',
    },
  },

  // BADGES - Status colors allowed here (badges are the exception)
  badges: {
    platform: { height: "28px", paddingX: "12px", paddingY: "4px", fontSize: "10px", fontWeight: 500, borderRadius: "8px", background: "rgba(255, 255, 255, 0.08)", border: "rgba(255, 255, 255, 0.15)", text: "rgba(255, 255, 255, 0.8)" },
    content: { paddingX: "10px", paddingY: "4px", fontSize: "12px", fontWeight: 500, borderRadius: "6px" },
    variants: {
      neutral: { background: "rgba(255, 255, 255, 0.06)", text: "rgba(255, 255, 255, 0.6)", border: "rgba(255, 255, 255, 0.15)" },
      success: { background: "rgba(52, 192, 124, 0.15)", text: "#34c07c", border: "rgba(52, 192, 124, 0.3)" },
      warning: { background: "rgba(234, 179, 8, 0.15)", text: "#eab308", border: "rgba(234, 179, 8, 0.3)" },
      error: { background: "rgba(239, 68, 68, 0.15)", text: "#ef4444", border: "rgba(239, 68, 68, 0.3)" },
    },
  },
  badgeTokens: {
    platform: { height: "28px", paddingX: "12px", paddingY: "4px", fontSize: "10px", fontWeight: 500, borderRadius: "8px", background: "rgba(255, 255, 255, 0.08)", border: "rgba(255, 255, 255, 0.15)", text: "rgba(255, 255, 255, 0.8)", glow: "0 0 12px rgba(255, 255, 255, 0.15)" },
    content: { height: "auto", paddingX: "10px", paddingY: "4px", fontSize: "12px", fontWeight: 500, borderRadius: "6px" },
    // Assistant Type Badges (AI / AUTO) - Compact tags for technology & pricing tier
    type: {
      base: { height: "20px", paddingX: "8px", paddingY: "3px", fontSize: "9px", fontWeight: 600, letterSpacing: "0.05em", borderRadius: "4px", textTransform: "uppercase" },
      variants: {
        // AI Badge - Premium, brighter white with subtle glow
        ai: { background: "rgba(255, 255, 255, 0.15)", border: "rgba(255, 255, 255, 0.25)", text: "#ffffff", glow: "0 0 8px rgba(255, 255, 255, 0.20)" },
        // AUTO Badge - Neutral, subtle utility feel
        auto: { background: "rgba(255, 255, 255, 0.06)", border: "rgba(255, 255, 255, 0.10)", text: "rgba(255, 255, 255, 0.6)", glow: "none" },
      },
    },
    // Status badge variants - colors allowed for badges only
    variants: {
      neutral: { background: "rgba(255, 255, 255, 0.08)", border: "rgba(255, 255, 255, 0.1)", text: "#a6a6a6" },
      info: { background: "rgba(255, 255, 255, 0.05)", border: "rgba(255, 255, 255, 0.08)", text: "#737373" },
      price: { background: "rgba(255, 255, 255, 0.08)", border: "rgba(255, 255, 255, 0.1)", text: "#a6a6a6" },
      primary: { background: "rgba(255, 255, 255, 0.12)", border: "rgba(255, 255, 255, 0.25)", text: "#ffffff", glow: "0 0 12px rgba(255, 255, 255, 0.15)" },
      active: { background: "rgba(52, 192, 124, 0.15)", border: "rgba(52, 192, 124, 0.3)", text: "#34c07c" },
      inactive: { background: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.3)", text: "#f59e0b" },
      success: { background: "rgba(52, 192, 124, 0.15)", border: "rgba(52, 192, 124, 0.3)", text: "#34c07c" },
      paused: { background: "rgba(234, 179, 8, 0.15)", border: "rgba(234, 179, 8, 0.3)", text: "#eab308" },
      stopped: { background: "rgba(115, 115, 115, 0.1)", border: "rgba(115, 115, 115, 0.2)", text: "#737373" },
      error: { background: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.3)", text: "#ef4444" },
      "no-activity": { background: "rgba(115, 115, 115, 0.1)", border: "rgba(115, 115, 115, 0.2)", text: "#737373" },
      connected: { background: "rgba(52, 192, 124, 0.15)", border: "rgba(52, 192, 124, 0.3)", text: "#34c07c" },
      "not-connected": { background: "rgba(115, 115, 115, 0.1)", border: "rgba(115, 115, 115, 0.2)", text: "#737373" },
      "in-progress": { background: "rgba(234, 179, 8, 0.15)", border: "rgba(234, 179, 8, 0.3)", text: "#eab308" },
      reconnect: { background: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.3)", text: "#f59e0b" },
      paid: { background: "rgba(52, 192, 124, 0.15)", border: "rgba(52, 192, 124, 0.3)", text: "#34c07c" },
      unpaid: { background: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.3)", text: "#f59e0b" },
      warning: { background: "rgba(234, 179, 8, 0.15)", border: "rgba(234, 179, 8, 0.3)", text: "#eab308" },
      // Sub-assistant badges
      sub: { background: "rgba(59, 130, 246, 0.12)", border: "rgba(59, 130, 246, 0.3)", text: "#3b82f6" },
      hybrid: { background: "rgba(168, 85, 247, 0.12)", border: "rgba(168, 85, 247, 0.3)", text: "#a855f7" },
      "coming-soon": { background: "rgba(255, 255, 255, 0.04)", border: "rgba(255, 255, 255, 0.08)", text: "rgba(255, 255, 255, 0.4)", fontStyle: "italic" },
      popular: { background: "rgba(234, 179, 8, 0.12)", border: "rgba(234, 179, 8, 0.3)", text: "#eab308" },
      "task-count": { background: "rgba(96, 165, 250, 0.12)", border: "rgba(96, 165, 250, 0.25)", text: "#60a5fa" },
    },
  },

  // =========================================================================
  // COMPONENT CATALOG — CSS utility classes from index.css (2026-02-20)
  // =========================================================================
  // Every CSS class family is documented here so new pages/modals can discover
  // production component patterns without reading index.css directly.

  components: {
    // NOTE: This catalog is manually maintained. index.css is the authoritative
    // source for class definitions. Verify against index.css when in doubt.

    // === SURFACES ===
    surfaces: {
      description: "Glass containers for sections, cards, and elevated panels",
      classes: {
        base: "glass-surface",              // Frosted panel: bg 0.04, blur 12px, border 0.10
        elevated: "glass-surface-elevated",  // Brighter: bg 0.06, blur 16px, stronger shadow
        card: "glass-card",                 // Hover-optimized: translateY(-2px) + aura glow
      },
      usage: "Use glass-surface for sections, glass-surface-elevated for modals/overlays, glass-card for interactive cards",
    },

    // === MODALS ===
    modals: {
      description: "Modal shell, tabs, and content structure",
      classes: {
        container: "modal-container",        // Glass shell with CSS vars
        sizeSm: "modal-sm",                 // Small modal (420px)
        sizeMd: "modal-md",                 // Medium modal (640px)
        sizeLg: "modal-lg",                 // Large modal (900px)
        header: "modal-header",              // Border-bottom + padding
        content: "modal-content",            // Scrollable body
        scrollable: "modal-scrollable",      // Custom thin scrollbar
        tab: "modal-tab",                   // Underline tab (inactive)
        tabActive: "modal-tab--active",     // Active tab with bottom line
      },
      usage: "Use modal-container + size class for new modals. modal-tab for tabbed content.",
    },

    // === SCROLLBAR (reference-only doc mirror of index.css :root vars) ===
    // Global luxury scrollbar — monochrome, translucent white. Applied to
    // every scrollable surface via *::-webkit-scrollbar + html (Firefox).
    // .scrollbar-hide still fully hides; .modal-scrollable uses a slimmer 6px
    // width with the same thumb tokens. Values must stay in sync with the
    // --scrollbar-* CSS vars in index.css :root.
    scrollbar: {
      description: "Global thin monochrome scrollbar tokens (mirror of index.css --scrollbar-*)",
      size: "10px",                          // --scrollbar-size (track/gutter width)
      thumb: "hsl(0 0% 100% / 0.14)",        // --scrollbar-thumb (idle)
      thumbHover: "hsl(0 0% 100% / 0.26)",   // --scrollbar-thumb-hover
      modalWidth: "6px",                     // .modal-scrollable slimmer variant
      hideClass: "scrollbar-hide",           // opt-out: fully hidden (mobile carousels)
    },

    // === BUTTONS ===
    buttons: {
      description: "Ghost action buttons for icon actions (play, pause, stop, close)",
      classes: {
        ghost: "ghost-button",                       // Minimal icon button
        ghostDestructive: "ghost-button--destructive", // Red hover for delete/stop
      },
      usage: "Use ghost-button for toolbar icon actions. Add --destructive for stop/delete.",
    },

    // === STATS / METRICS ===
    stats: {
      description: "Metric display patterns for dashboards and modals",
      classes: {
        tile: "stat-tile",                   // Hero metric (2x2 grid cell)
        tileLabel: "stat-tile-label",        // 12px muted label above value
        tileValue: "stat-tile-value",        // Large mono number
        table: "stat-table",               // Key-value rows container
        tableRow: "stat-table-row",         // flex space-between row
        tableLabel: "stat-table-label",     // Left-aligned muted label
        tableValue: "stat-table-value",     // Right-aligned mono value
        metricsCard: "metrics-card",         // Dashboard KPI card (extends glass-card)
        metricsCardFocal: "metrics-card--focal",       // Page-lead focal tile (more padding)
        metricsCardLabel: "metrics-card-label",
        metricsCardValue: "metrics-card-value",        // text-metric, weight 500, clamp(20-28px)
        metricsCardValueFocal: "metrics-card-value--focal", // PAGE-LEAD number, weight 300, clamp(36-44px)
        metricsCardValueLead: "metrics-card-value--lead",   // Prominent KPI (not page-lead), weight 500, clamp(26-34px)
        metricsCardSub: "metrics-card-sub",
        metricsCardAttention: "metrics-card--attention",    // Needs-attention pulse border
        stagger: "metrics-stagger",          // Entrance animation wrapper
        kpiCard: "kpi-card",               // Standalone KPI (billing/pulse)
        kpiCardLabel: "kpi-card-label",
        kpiCardValue: "kpi-card-value",
        kpiCardSub: "kpi-card-sub",
      },
      usage: "Modal stats: stat-tile (hero grid) + stat-table (details). Dashboard: metrics-card or kpi-card.",
    },

    // === LOGS / ACTIVITY TIMELINE ===
    logs: {
      description: "Activity timeline: small status dots threaded on ONE quiet vertical rail. No icons, no per-row dividers. Monochrome — dot color only for problems (red = failed, amber = needs attention); everything else neutral, text carries the meaning.",
      classes: {
        rail: "activity-rail",               // Container — relative anchor for the rail
        row: "activity-row",                 // One event row; draws its rail segment (::before)
        dot: "activity-row__dot",            // Status dot on the rail (combine with .status-dot--*)
      },
      usage: "Wrap rows in .activity-rail; each .activity-row holds an .activity-row__dot + .status-dot--{muted|error|warning}. Use .text-eyebrow for date-group labels. Status color reserved for failed/alert — lifecycle + success stay .status-dot--muted.",
    },

    // === CONNECTIONS ===
    connections: {
      description: "App connection cards (provider rows in modals and pages)",
      classes: {
        card: "connection-card",             // Row with icon + status
        icon: "connection-card-icon",       // 36x36 icon container
      },
      usage: "Use connection-card for OAuth provider rows. Add status-dot for health.",
    },

    // === FILTERS / INPUTS ===
    filters: {
      description: "Filter chips, search inputs, and toggle bars",
      classes: {
        chip: "filter-chip",                         // Toggle button (inactive)
        chipActive: "filter-chip--active",           // Active state with border
        input: "input-field",                        // Search/text input
      },
      usage: "Use filter-chip for log/table filters. input-field for search bars.",
    },

    // === QUICK-SELECT + OTHER ===
    // Any field with a bounded/recommendable answer set (industry, founder
    // role, platform name, rating) gets a curated quick-select + "Other"
    // free-type escape — never a bare free-text input or number spinner.
    // Codified 2026-07-09 (#2137 — Brain reputation form shipped "Platform"
    // as free text and "Rating" as a 0-5 number spinner).
    quickSelectOrOther: {
      description: "Curated quick-select + \"Other\" escape hatch for any bounded/recommendable field — replaces bare free-text inputs and number spinners",
      classes: {
        compact: "SelectOrOther (component)",   // src/components/ui/SelectOrOther.tsx — native <select> seeded with curated options + auto-appended "Other…" sentinel; picking it reveals a free-text (or, via otherInputProps, numeric) input. Wire via AutoSaveField variant="select-or-other" (Brain forms) or use directly (OutwardReputation.tsx — numeric Rating via otherInputProps).
        richPicker: "ProviderPickerModal (component)", // src/components/connections/ProviderPickerModal.tsx — the same principle as a full picker-modal/vertical-list flow (recommended + coming-soon pills) for choices that need richer visual rows (icons, descriptions, connection-status) rather than a compact field-level select.
      },
      usage: "Compact field-level choice → SelectOrOther. Richer list/modal choice → ProviderPickerModal's pattern. Never ship a bare <input> or number spinner for a bounded/recommendable answer set.",
    },

    // === COMPREHENSION CHIPS ===
    // De-boxed count pills for the Brain Comprehension Overview band (the
    // "N things known / missing / to re-read / mismatch" doorway, design
    // §8.1.1). Rounded-full hairline pill, ≥44px stacked tap target. The glyph
    // carries the semantic colour (emerald = known, amber = flagged); the text
    // stays white so colour never does the job alone (glyph+text, never colour).
    comprehension: {
      description: "Count chips for the Brain Comprehension Overview band",
      classes: {
        chip: "comprehension-chip",
      },
      usage: "comprehension-chip = a tappable count pill that opens the full-read sheet pre-filtered.",
    },

    // === BRAIN JUMP NAV ===
    // Typographic TOC (2026-08-18). Group labels stay; bordered pills retired.
    jumpNav: {
      description: "Brain playbook jump-nav — typographic TOC, no pills",
      classes: {
        toc: "jn-toc",
        row: "jn-toc-row",
        group: "jn-toc-g",
        link: "jn-link",
        linkActive: "jn-link--active",
        node: "jn-node",
        sep: "jn-sep",
        top: "jn-top-link",
        trigger: "jn-trigger min-h-[44px]",
        sheetGroup: "jn-sheet-g",
        sheetLink: "jn-sheet-link min-h-[44px]",
        sheetLinkActive: "jn-sheet-link--active",
      },
      usage: "Desktop: two quiet TOC rows. <lg: one 44px trigger that opens a sheet of the same TOC.",
    },

    // === BADGES ===
    badges: {
      description: "Status indicators and content labels",
      classes: {
        platform: "badge-platform",                  // Tiny uppercase (PRIVATE BETA)
        platformPreview: "badge-platform--preview",  // retired from header — warning only
        platformPrivateBeta: "badge-platform--private-beta",
        label: "badge-label",                        // Content label (status, price)
        // Semantic variants (representative, not exhaustive — see index.css for full list):
        // --neutral, --info, --price, --primary, --active, --success,
        // --connected, --paid, --paused, --warning, --in-progress, --error,
        // --stopped, --not-connected, --accent, --test, --sub, --hybrid,
        // --coming-soon, --popular
        typeAI: "badge-type--ai",                    // AI technology tag
        typeAuto: "badge-type--auto",                // AUTO technology tag
      },
      usage: "badge-platform for nav/header. badge-label + variant for card/modal status. badge-type for tech tags.",
    },

    // === STATUS ===
    status: {
      description: "Small health indicator dots",
      classes: {
        dot: "status-dot",                           // 6px circle
        dotSuccess: "status-dot--success",
        dotWarning: "status-dot--warning",
        dotError: "status-dot--error",
        dotMuted: "status-dot--muted",
      },
      usage: "Use status-dot + variant inside connection-card or summary cards.",
    },

    // === PAGE STRUCTURE ===
    pageStructure: {
      description: "Page structure and section headings",
      classes: {
        pageContainer: "page-container",              // Full-width page wrapper
        pageHeader: "page-header",                    // Title + description
        pageHeaderActions: "page-header--with-actions", // Header + buttons
        sectionHeader: "section-header",              // Section title (14px medium)
        sectionHeaderSub: "section-header-sub",       // Subtitle (12px muted)
        eyebrow: "text-eyebrow",                      // Uppercase kicker (13px / 0.14em / white-0.4)
        sectionThread: "section-thread",              // Lit 1px hairline thread (consumes materials.hairlineStrong)
        introGuide: "PageIntroGuide (component)",
      },
      usage: "Wrap every page in page-container. Use section-header above stat blocks.",
      // LEAN-SECTION COMPOSITION (premium-flow pass, 2026-06-09):
      // The premium voice is calm composition — separate sections by SPACE and a
      // lit hairline, NOT by wrapping each in a `rounded-2xl border` glass box
      // (which reads "admin panel"). The line/surface vocabulary it consumes is
      // already catalogued in `materials.*` (hairline / hairlineStrong /
      // surfaceFaint) — this is a COMPOSITION recipe over those tokens, not a new
      // family. Recipe for a lean section:
      //   1. Head it with <SectionMarker label="…" thread /> — renders the
      //      `.text-eyebrow` kicker + the `.section-thread` lit hairline.
      //   2. Recessive supporting copy: text-[13px] text-white/40 font-light
      //      leading-relaxed — quiet, secondary, never competing with the title.
      //   3. The panel itself: `.surface-hairline` (hairline border over faint
      //      fill) for a lean panel, or `.list-hairline` for a divided row list —
      //      NOT a glass box. Let whitespace (mb-6 / space-y) be the separation.
      // HIERARCHY TIERS (added 2026-06-10 — de-boxing flattens scale; rebuild it):
      // Removing boxes also removes the containment that signalled grouping +
      // nesting. Restore scan-hierarchy with type-step + indent, NEVER by
      // re-adding a box. Three tiers:
      //   T2 — GROUP header: <SectionMarker> eyebrow kicker (zone label).
      //   T3 — SECTION anchor: title at text-base (16px) font-medium, sitting
      //        above a 13px white/40 font-light sub — the title clearly leads.
      //   T4 — NESTED content: indent under the title column + a quiet left rail
      //        (`border-l` consuming materials.hairline, e.g. `ml-2 border-l
      //        pl-5 sm:pl-6`) so expanded/child content reads as INSIDE its
      //        section, not as another peer row. Reference: BrainAccordionItem.
      //        Lean ≠ flat — see kit design-lessons `de-boxing-flattens-hierarchy`.
      // PAGE HEADER ZONE (LOCKED layout standard, 2026-06-10 — kit
      // design-decisions `page-header-zone`):
      // Every page opens with ONE header zone of fixed-order, pluggable slots.
      // Consistency = consistent PARTS, not identical bodies — pages legitimately
      // differ on which optional slots they use; never force uniformity.
      //   ① TITLE          — PageLayout h1 (.page-header h1, 28px font-light)  ALWAYS
      //   ② SUB-HEADING    — 16px white/40 font-light (.page-header p)         ALWAYS
      //   ③ INTRO GUIDE    — "How X works": open-by-default desktop,          OPTIONAL
      //                       collapse-to-toggle on mobile
      //   ④ HEADER HAIRLINE — a --hairline divider closing the zone            ALWAYS
      //   ⑤ TAB BAR        — page tabs render inside the zone (no scroll)      OPTIONAL
      //                       Default: under the hairline. Pulse HQ
      //                       (`tabsPlacement="header-right"`) puts the
      //                       lifecycle tabs on the title row — pulse.html
      //                       `.page-head` / lit underline.
      //   → then content: SectionMarker eyebrows + the T2/T3/T4 tier ladder
      //     + tight-cards, de-boxed.
      // ①②④ are always present and IDENTICAL on every page (the PageLayout
      // `description` prop stays optional in TS, but a page shipping without ②
      // is a rollout gap to close in that page's PR, not an allowed variant).
      // ③⑤ are optional but render identically wherever they appear — one
      // shared style, never restyled per page. Eyebrow kickers title SECTIONS only — never above
      // the page title. Slot usage: Pulse/Team = tabs+intro · Billing = tabs ·
      // Assistants/Connects = intro · Account = neither. PageLayout owns the zone —
      // pages plug slots in via props, they don't rebuild the zone locally.
    },

    // === INTERACTION ===
    interactions: {
      description: "Hover effects and selection states",
      classes: {
        hover: "interactive-hover",                  // White tint + aura on hover
        hoverGlow: "interactive-hover-glow",         // Glow + white text on hover
        selected: "interactive-selected",            // Solid white fill (selected pill)
        hoverGlowOnly: "hover-glow",               // Just aura shadow on hover
      },
      usage: "Add interactive-hover for generic list items. interactive-selected for active filters/nav.",
    },

    // === LOADING ===
    loading: {
      description: "Skeleton and animation states",
      classes: {
        skeleton: "skeleton",                        // Shimmer loading placeholder
        pulseGlow: "animate-pulse-glow",             // Repeating accent pulse — retire on idle chrome
        sealSpin: "brand-seal-spin",                 // U13: Seal · mid while work runs
        sealStamp: "brand-seal-stamp",               // U13: one-shot done press
      },
      usage: "Prefer SealSpinner / ProgressThread / ThinkingNodes / SealStamp from src/components/brand/. Add skeleton class to placeholder divs. Set width/height via Tailwind.",
    },

    // === BILLING ARTIFACT PRIMITIVES ===
    // Tokenized from the #2576 Billing IA collapse (Peyton-approved artifact).
    // Pointer entries only — values live in index.css; see the archetype at
    // docs/design/archetypes/usage-billing-surface.md.
    billingArtifacts: {
      description: "Flat-row and money-focal idiom from the Billing IA collapse (#2576), extracted for platform-wide reuse",
      classes: {
        rowsFlat: "rows-flat",                        // Flat hairline row group: bounding border-y + per-row divide-y
        moneyFocal: "money-focal",                    // De-boxed hero dollar figure (extralight, tracking-tight, tabular-nums)
        pageTitleCompact: "page-title-compact",       // 17px / weight 400 compact page title
        metaInline: "meta-inline",                    // 11.5px white/30 inline row meta (e.g. trailing date)
      },
      usage: "Use rows-flat for a hairline-divided row list on page bg (not a glass card). money-focal for a page-lead balance/dollar figure. page-title-compact for slim non-PageLayout titles. meta-inline for a date/meta fragment trailing a row label.",
    },

    // === SHELL PRIMITIVES (#2576 Wave 1b) ===
    shellPrimitives: {
      description: "Small shared components introduced for the #2576 shell/redesign program — pointer entries only, the components are the source of truth",
      classes: {
        instanceId: "InstanceIdChip (component)", // src/components/common/InstanceIdChip.tsx — C6 instance identity: quiet mono `#XYZ` chip (trailing 3 of assistants.display_id, e.g. "ASST-4K2XYZ" → "#XYZ"), title-adjacent AFTER the instance name. The nickname is surrounding text, never inside the chip. Use anywhere the same template has multiple activations (Pulse cards/Breakdown/Attention/Activity, Usage cost table).
        runControls: "RunControls (component)",   // src/components/common/RunControls.tsx — C9 pause/resume/stop icon+label affordances, aria-labeled, >=44px touch targets. status: 'active'|'paused'|'stopped'. Wired into Pulse cards + post-detail modal in a later PR.
        attachmentChip: "AttachmentChip (component)", // src/components/common/AttachmentChip.tsx — ONE dropped-file / attached-image visual (brain.html .stage-row / .attach-chip). density row (40px confirm list) or chip (composer, feedback, launch kit). Thumb + filename + ✕. 3+ items: AttachmentStack (header + first preview, rest open in place). Never a per-item intent picker.
      },
      usage: "Reach for these instead of re-inventing an instance-identity chip or a run-control cluster on a new surface — one recipe, everywhere an assistant instance is named or controlled.",
    },

    // === SUCCESS SEMANTICS (C12) ===
    // Pointer entry only — the real value lives in index.css (--success-emerald).
    successSemantics: {
      description: "C12 (Peyton ruling 2026-07-20, #2576) — emerald (#34c07c, --success-emerald) is the platform's POSITIVE/SUCCESS signal, not a money color.",
      color: "--success-emerald", // #34c07c — use this, not the older HSL --success token below
      carries: [
        "wallet CREDITS (+$ top-ups/refunds)",
        "positive-outcome banners (e.g. \"Continue Activation\")",
        "confirmation ticks",
        "connected/healthy status",
      ],
      neverFor: "NEUTRAL money — balance figures, the money-focal hero, debit amounts (e.g. −$0.08) stay monochrome. Emerald is a success signal, not a money color.",
      usage: "Reach for --success-emerald (or the connectionStatus.healthy Tailwind emerald-400/500 family, already compliant) for the specific carriers above. designSystem.colors.success/badges/badgeTokens success-family entries were swept 2026-07-30 (design-sor-consolidation Option A) from the old retired shadcn green (hex 22c55e) to this same #34c07c, so they now agree — no more conflict there. The CSS --success/--success-foreground HSL pair in index.css also feeds tailwind.config.ts's `success` class family (text-success/bg-success/* — 15+ live call sites); rather than sweep every call site, the HSL VALUE was repointed to the same emerald hue 2026-07-31, so those classes render emerald too — don't add new direct var(--success) consumers, use --success-emerald for those.",
    },
  },

  // =========================================================================
  // PREVIEW — step-1 Personalize live preview (T1052, blessed 2026-06-10)
  // =========================================================================
  // PreviewPanel frame + device chrome + placeholder tokens. Locked taste
  // calls (Peyton): minimal hairline device chrome (never skeuomorphic, never
  // colored) · quiet `·· PREVIEW ··` watermark at eyebrow type, 0.30 alpha ·
  // static 135° diagonal-hatch placeholder with dashed border + REQUIRED
  // label routing to Try. The non-removable honesty strip carries the trust
  // load; the watermark stays calm. NOT the `.skeleton` shimmer — a skeleton
  // says "loading", this placeholder says "AI writes this when it runs".
  // CSS utilities: `.preview-frame`, `.preview-watermark`,
  // `.preview-placeholder` in index.css consume these values.

  preview: {
    // Frame — the rail container around any device shell
    frame: {
      background: "rgba(255, 255, 255, 0.02)",
      border: "1px solid rgba(255, 255, 255, 0.08)",
      radius: "16px",
    },
    // Watermark — `·· PREVIEW ··` in the frame header, never inside the artifact
    watermark: {
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.18em",
      textTransform: "uppercase" as const,
      opacity: 0.3,
    },
    // Device chrome — hairline monochrome shells (phone / browser-card)
    chrome: {
      hairline: "1px solid rgba(255, 255, 255, 0.10)",
      hairlineSubtle: "1px solid rgba(255, 255, 255, 0.06)",
      surface: "rgba(255, 255, 255, 0.03)",
      phoneRadius: "28px",
      browserRadius: "12px",
      notchColor: "rgba(255, 255, 255, 0.10)",
      urlBarColor: "rgba(255, 255, 255, 0.30)",
    },
    // Placeholder — static 135° hatch for content only AI generation produces.
    // Label is REQUIRED (honesty law) and must say: (a) AI writes this when
    // the assistant runs, (b) it appears on test/activation, (c) see it in Try.
    placeholder: {
      hatch:
        "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 7px)",
      border: "1px dashed rgba(255, 255, 255, 0.15)",
      radius: "10px",
      labelColor: "rgba(255, 255, 255, 0.55)",
    },
    // Honesty strip — non-removable bottom strip on every PreviewPanel
    honesty: {
      text: "Not live yet · nothing sent, nothing saved.",
      representativeSuffix: " · styling shown is representative",
      color: "rgba(255, 255, 255, 0.40)",
      borderTop: "1px solid rgba(255, 255, 255, 0.06)",
    },
  },

  // =========================================================================
  // TOKEN MIGRATION — designSystem token → Tailwind/CSS class equivalent
  // =========================================================================
  // Manually maintained. Use this to progressively replace
  // `style={{ color: designSystem.colors.X }}` with className equivalents.
  // Verify against tailwind.config.ts if a mapping looks wrong.

  tokenMigration: {
    colors: {
      textPrimary: "text-foreground",
      textSecondary: "text-muted-foreground",
      textMuted: "text-muted-foreground/70",         // or text-white/40
      background: "bg-background",
      surface: "bg-white/[0.04]",
      surfaceElevated: "bg-white/[0.06]",
      surfaceGlass: "bg-white/[0.08]",
      border: "border-border",                       // or border-white/10
      borderSubtle: "border-white/[0.06]",
      borderHover: "border-white/[0.15]",
      accent: "bg-white text-black",                 // context-dependent
      accentSubtle: "bg-white/[0.08]",
      success: "text-[#34c07c]",                     // C12 emerald — not text-green-500 (retired 2026-07-30)
      warning: "text-yellow-500",
      destructive: "text-destructive",
      info: "text-blue-500",
      activate: "bg-[#166534] hover:bg-[#15803d]",
      favorite: "text-red-900",
    },
    typography: {
      sizes: {
        xs: "text-xs",
        small: "text-sm",
        base: "text-base",
        body: "text-base",
        cardTitle: "text-lg",
        sectionHeading: "text-2xl",
        // Page titles use the custom .text-page-title class (clamp 24→28px), NOT
        // this text-3xl Tailwind mapping — see typography.sizes.pageTitle above.
        pageTitle: "text-3xl",
      },
      weights: {
        light: "font-light",
        regular: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        // DEPRECATED — do not use. Geist's heaviest UI weight is semibold; the
        // anti-bold guardrail fails the build on any new `font-bold`. Kept only
        // so existing type references don't break. (T1126)
        bold: "font-bold",
      },
      fontFamily: "font-sans",
      fontFamilyMono: "font-mono",
    },
    radius: {
      sm: "rounded-lg",
      md: "rounded-xl",
      lg: "rounded-2xl",
      xl: "rounded-[20px]",
      full: "rounded-full",
    },
    spacing: {
      // Spacing maps to gap or padding depending on context
      xs: { gap: "gap-1", padding: "p-1" },
      sm: { gap: "gap-2", padding: "p-2" },
      md: { gap: "gap-4", padding: "p-4" },
      lg: { gap: "gap-6", padding: "p-6" },
      xl: { gap: "gap-8", padding: "p-8" },
    },
  },
};

export type DesignSystem = typeof designSystem;
